import Phaser from 'phaser';
import type { DestructibleDef } from '@/domain/types/destructible';
import type { CurrencyId } from '@/domain/types/common';
import { DestructibleAnimator } from '@/game/fx/DestructibleAnimator';
import type { PetTarget } from './PetTarget';

/**
 * Pojedyncza instancja destructibla na mapie.
 *
 * Cykl:
 *   alive → takeDamage(...) → (hit reaction) → ... → HP=0 → death animation
 *   → destroyed (sprite ukryty) → respawn po `def.respawnSec` → alive
 */
export type DestructibleState = 'alive' | 'dying' | 'destroyed';

export interface RolledReward {
  currency: CurrencyId;
  amount: number;
}

export class Destructible implements PetTarget {
  readonly sprite: Phaser.GameObjects.Image;
  private hpBarBg: Phaser.GameObjects.Rectangle;
  private hpBarFill: Phaser.GameObjects.Rectangle;
  private label?: Phaser.GameObjects.Text;
  private idleGlow?: Phaser.GameObjects.Image;
  private animator: DestructibleAnimator;
  private currentHp: number;
  state: DestructibleState = 'alive';

  /** Lista aktywnych petów namierzających ten obiekt. */
  readonly attackers = new Set<string>();

  constructor(
    private readonly scene: Phaser.Scene,
    readonly def: DestructibleDef,
    readonly position: { x: number; y: number },
    textureKey: string,
    private readonly onDestroyed?: (self: Destructible) => void,
  ) {
    this.currentHp = def.hpBase;

    this.sprite = scene.add.image(position.x, position.y, textureKey);
    this.sprite.setDisplaySize(def.size.w, def.size.h);
    this.sprite.setData('destructible', this);
    this.animator = new DestructibleAnimator(scene, this.sprite);
    this.createIdleGlow();

    const barW = def.size.w * 0.9;
    const barY = position.y - def.size.h / 2 - 10;
    this.hpBarBg = scene.add.rectangle(position.x, barY, barW, 6, 0x000000, 0.6);
    this.hpBarFill = scene.add.rectangle(position.x - barW / 2, barY, barW, 6, 0xe74c3c).setOrigin(0, 0.5);
    this.hpBarBg.setVisible(false);
    this.hpBarFill.setVisible(false);

    if (def.art.showLabel !== false) {
      this.label = scene.add.text(position.x, position.y - def.size.h / 2 - 24, def.name.pl, {
        fontFamily: 'Baloo 2, sans-serif',
        fontSize: '13px',
        color: '#1a1a1a',
      }).setOrigin(0.5);
    }
  }

  private createIdleGlow(): void {
    const glow = this.def.art.idleGlow;
    if (!glow) return;

    // A single inexpensive gradient sprite per target also works on Canvas.
    // No particles or individual glow shaders are needed for the 24 pumpkins.
    const key = 'fx-destructible-idle-glow';
    if (!this.scene.textures.exists(key)) {
      const texture = this.scene.textures.createCanvas(key, 128, 128);
      if (!texture) return;
      const context = texture.context;
      const gradient = context.createRadialGradient(64, 64, 0, 64, 64, 64);
      gradient.addColorStop(0, 'rgba(255,255,255,1)');
      gradient.addColorStop(0.35, 'rgba(255,255,255,0.8)');
      gradient.addColorStop(0.7, 'rgba(255,255,255,0.25)');
      gradient.addColorStop(1, 'rgba(255,255,255,0)');
      context.fillStyle = gradient;
      context.fillRect(0, 0, 128, 128);
      texture.refresh();
    }

    this.idleGlow = this.scene.add.image(this.sprite.x, this.sprite.y, key)
      .setDisplaySize(this.def.size.w * 1.75, this.def.size.h * 1.5)
      .setTint(glow.color)
      .setAlpha(glow.alpha)
      .setDepth(this.sprite.depth - 1);
  }

  get x(): number { return this.position.x; }
  get y(): number { return this.position.y; }

  /** PetTarget: czy pet może go namierzyć (żywy). Hartuje też celowanie w świecie. */
  isTargetable(): boolean {
    return this.state === 'alive';
  }

  hasFreeSlot(): boolean {
    return this.isTargetable() && this.attackers.size < this.def.maxPetSlots;
  }

  /** Zadaj obrażenia. Zwraca true jeśli destructible umarł w tym wywołaniu. */
  takeDamage(amount: number): boolean {
    if (this.state !== 'alive') return false;
    this.currentHp = Math.max(0, this.currentHp - amount);
    this.updateHpBar();
    this.animator.triggerHit();
    if (this.currentHp <= 0) {
      this.die();
      return true;
    }
    return false;
  }

  rollRewards(): RolledReward[] {
    const out: RolledReward[] = [];
    for (const roll of this.def.rewards) {
      if (Math.random() > roll.chance) continue;
      const amount = Phaser.Math.Between(roll.min, roll.max);
      out.push({ currency: roll.currency, amount });
    }
    return out;
  }

  /** Per-frame tick — wywoływany przez WorldScene.update(). */
  tick(time: number): void {
    if (this.state === 'alive' || this.state === 'dying') {
      this.animator.update(time);

      // HP bar follow (gdy idle pulse modyfikuje x/y obiektu, bar też się rusza)
      const barY = this.sprite.y - this.def.size.h / 2 - 10;
      this.hpBarBg.x = this.sprite.x;
      this.hpBarBg.y = barY;
      this.hpBarFill.y = barY;
      this.hpBarFill.x = this.sprite.x - this.def.size.w * 0.9 / 2;
      if (this.label) {
        this.label.x = this.sprite.x;
        this.label.y = this.sprite.y - this.def.size.h / 2 - 24;
      }
      this.idleGlow?.setPosition(this.sprite.x, this.sprite.y);
    }
  }

  private updateHpBar(): void {
    const ratio = this.currentHp / this.def.hpBase;
    const fullW = this.def.size.w * 0.9;
    this.hpBarFill.width = fullW * ratio;
    const visible = ratio < 1 && this.state === 'alive';
    this.hpBarBg.setVisible(visible);
    this.hpBarFill.setVisible(visible);
  }

  private die(): void {
    this.state = 'dying';
    this.hpBarBg.setVisible(false);
    this.hpBarFill.setVisible(false);
    this.label?.setVisible(false);
    this.idleGlow?.setVisible(false);
    this.attackers.clear();

    // Callback (np. spawn coinów) DZIEJE się od razu — gracze chcą widzieć monetki
    // wystrzeliwujące w trakcie death animacji.
    this.onDestroyed?.(this);

    // Death animation — dopiero po niej przechodzimy do 'destroyed' i schedulujemy respawn
    this.animator.playDeathAnimation().then(() => {
      this.state = 'destroyed';
      this.sprite.setVisible(false);
      this.scene.time.delayedCall(this.def.respawnSec * 1000, () => this.respawn());
    });
  }

  private respawn(): void {
    this.currentHp = this.def.hpBase;
    this.state = 'alive';
    this.sprite.setVisible(true);
    this.animator.resetAfterRespawn();
    this.label?.setVisible(true);
    this.idleGlow?.setVisible(true);
    this.updateHpBar();
  }
}
